import * as React from 'react';
import { createContext, useContext, useState, useMemo, ReactNode } from 'react';
import {
  translations,
  Language,
  TranslationKeys,
  TranslationVariables,
  NestedKeyOf,
} from '../language';

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  translations: TranslationKeys;
}

// Create the context with undefined as initial value
export const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  // Default no-op implementation for context initialization
  setLang: (_lang: Language) => {
    console.log(_lang);
  },
  translations: translations['en'] as TranslationKeys,
});

interface LanguageProviderProps {
  children: ReactNode;
  defaultLang?: Language;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  defaultLang = 'en',
}) => {
  const [lang, setLang] = useState<Language>(defaultLang);

  const value = useMemo<LanguageContextType>(
    () => ({
      lang,
      setLang,
      translations: translations[lang] as TranslationKeys,
    }),
    [lang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

interface UseTranslationReturn {
  t: (key: NestedKeyOf<TranslationKeys>, vars?: TranslationVariables) => string;
  lang: Language;
  setLang: (lang: Language) => void;
  availableLanguages: Language[];
}

export const useTranslation = (): UseTranslationReturn => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useTranslation must be used within LanguageProvider');
  }

  const { lang, setLang, translations } = context;

  const t = (key: NestedKeyOf<TranslationKeys>, vars?: TranslationVariables): string => {
    const keys = key.split('.');
    let result: unknown = translations;

    // Navigate through nested object
    for (const k of keys) {
      if (result && typeof result === 'object') {
        result = (result as Record<string, unknown>)[k];
      } else {
        console.warn(`Translation key not found: ${key}`);
        return key;
      }
    }

    if (typeof result !== 'string') {
      console.warn(`Translation key is not a string: ${key}`);
      return key;
    }

    // Replace variables {variable}
    let text = result;
    if (vars) {
      Object.keys(vars).forEach((k) => {
        text = text.replace(new RegExp(`\\{${k}\\}`, 'g'), String(vars[k]));
      });
    }

    return text;
  };

  const availableLanguages = Object.keys(translations) as Language[];

  return {
    t,
    lang,
    setLang,
    availableLanguages,
  };
};
